import { AuthState } from '@packages/@types/authContext';
import UserController from '../../../controllers/user.controller';
import ClientController from '../../../controllers/client.controller';
import ClientFeatureConfigController from '../../../controllers/clientFeatureConfig.controller';
import {
    APIForwarder,
    AutoStacking,
    ClientFeatureConfig,
    FEATURES,
    FullEta,
    JSONTransform,
    PreparationTime,
    Webhook,
    ClosingTool,
    E2EFlow,
} from '../../../types/Feature';
import { User } from '../../../types/User';
import { Role } from '../../../types/Auth';
import { ClientCredentials } from '../../../types/Client';
import { FeaturesFormData } from '../types';
import FEATURE_TYPE from '../../../../../server/src/shared/enums/FeatureTypes';
import {
    DriversEtaAttributes,
    FleetsConfigAttributes,
    PriceMatrixAttributes,
    QuoteDenyingConfigAttributes,
    SmartFleetChoiceAttributes,
} from '../../../../../server/src/models';

export const getAllUsers = (): Promise<User[]> => UserController.getAllUsers();

export const getFleetsFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<FleetsConfigAttributes>> => {
    const [fleets] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.FLEETS,
    );
    return fleets as ClientFeatureConfig<FleetsConfigAttributes>;
};

export const getAutostackingFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<AutoStacking>> => {
    const [autostacking] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.AUTO_STACKING,
    );
    return autostacking as ClientFeatureConfig<AutoStacking>;
};

export const getWebhookFeatureConfig = async (clientId: string | number): Promise<ClientFeatureConfig<Webhook>> => {
    const [webhook] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.WEBHOOK,
    );
    return webhook as ClientFeatureConfig<Webhook>;
};

export const getApiForwarderFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<APIForwarder>> => {
    const [apiForwarder] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.API_FORWARDER,
    );
    return apiForwarder as ClientFeatureConfig<APIForwarder>;
};

export const getJSONTransformFeatureConfig = async (
    clientId: string | number,
    user: AuthState['user'] | null,
): Promise<Array<ClientFeatureConfig<JSONTransform>> | undefined> => {
    if (!user || (user.role !== Role.StuartStaff && user.role !== Role.SE)) return undefined;

    const jsonTransform = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.JSON_TRANSFORM,
    );
    return jsonTransform as Array<ClientFeatureConfig<JSONTransform>>;
};

export const getPriceMatrixFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<PriceMatrixAttributes>> => {
    const [priceMatrix] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.PRICE_MATRIX,
    );
    return priceMatrix as ClientFeatureConfig<PriceMatrixAttributes>;
};

export const getDriversEtaFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<DriversEtaAttributes>> => {
    const [driversEta] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURE_TYPE.DRIVERS_ETA,
    );

    return driversEta as ClientFeatureConfig<DriversEtaAttributes>;
};

export const getSmartFleetChoiceFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<SmartFleetChoiceAttributes>> => {
    const [smartFleetChoice] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURE_TYPE.SMART_FLEET_CHOICE,
    );

    return smartFleetChoice as ClientFeatureConfig<SmartFleetChoiceAttributes>;
};

export const getQuoteDenyingFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<QuoteDenyingConfigAttributes>> => {
    const [quoteDenying] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURE_TYPE.QUOTE_DENYING,
    );

    return quoteDenying as ClientFeatureConfig<QuoteDenyingConfigAttributes>;
};

export const getClientCredentials = async (clientId: string | number): Promise<ClientCredentials> => {
    const credentials = await ClientController.getCredentials(Number(clientId));
    return credentials;
};

export const getFullEtaFeatureConfig = async (clientId: string | number): Promise<ClientFeatureConfig<FullEta>> => {
    const [fullEta] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.FULL_ETA,
    );
    return fullEta as ClientFeatureConfig<FullEta>;
};

export const getPreparationTiemFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<PreparationTime>> => {
    const [preparationTime] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.PREPARATION_TIME,
    );
    return preparationTime as ClientFeatureConfig<PreparationTime>;
};

export const getClosingToolFeatureConfig = async (
    clientId: string | number,
): Promise<ClientFeatureConfig<ClosingTool>> => {
    const [closingTool] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.CLOSING_TOOL,
    );
    return closingTool as ClientFeatureConfig<ClosingTool>;
};

export const getE2EFlowFeatureConfig = async (clientId: string | number): Promise<ClientFeatureConfig<E2EFlow>> => {
    const [scheduled] = await ClientFeatureConfigController.getAllFeatureConfigByClientId(
        Number(clientId),
        FEATURES.E2E_FLOW,
    );
    return scheduled as ClientFeatureConfig<E2EFlow>;
};

export const getAllFeatureConfigs = async (
    clientId: string | number,
    user: AuthState['user'] | null,
): Promise<FeaturesFormData> => ({
    autostacking: await getAutostackingFeatureConfig(clientId),
    webhook: await getWebhookFeatureConfig(clientId),
    apiForwarder: await getApiForwarderFeatureConfig(clientId),
    jsonTransform: await getJSONTransformFeatureConfig(clientId, user),
    clientCredentials: await getClientCredentials(clientId),
    fleets: await getFleetsFeatureConfig(clientId),
    priceMatrix: await getPriceMatrixFeatureConfig(clientId),
    driversEta: await getDriversEtaFeatureConfig(clientId),
    smartFleetChoice: await getSmartFleetChoiceFeatureConfig(clientId),
    quoteDenying: await getQuoteDenyingFeatureConfig(clientId),
    fullEta: await getFullEtaFeatureConfig(clientId),
    preparationTime: await getPreparationTiemFeatureConfig(clientId),
    closingTool: await getClosingToolFeatureConfig(clientId),
    e2eFlow: await getE2EFlowFeatureConfig(clientId),
});
